// Per-upload shared state for connector dispatchers.
//
// A single PowerSync CRUD batch can contain an optimistic parent insert
// (asset_type, or a client-id capture_session) followed by child rows
// that reference it (capture_items, spaces, floors). If the parent
// upload races and the server returns an authoritative replacement id,
// later entries in the same batch must post against that replacement id
// instead of the now-deleted optimistic id.
//
//   assetTypeIdRewrites — orphan asset_type id → authoritative id.
//     Consumed by the capture_items POST (the item carries asset_type_id
//     in its op data).
//
//   sessionIdRewrites — orphan capture_session id → authoritative id.
//     Consumed by the capture_items POST (the item carries
//     capture_session_id in its op data). Set only when the authoritative
//     session is ACTIVE (completed_at IS NULL); a completed authoritative
//     is left to the bare-drop path so re-homed items don't wedge the
//     queue against a completed session. See connector-dispatch-session-
//     insert.ts.
//
//   sessionRehomeByBuilding — building id → authoritative session id.
//     spaces/floors don't carry the session id in their op data; their
//     dispatchers recover it from the building via loadSessionIdForBuilding.
//     When a session was re-homed this batch the orphan local row is gone,
//     so that lookup must prefer the authoritative id keyed by building
//     rather than racing the read-sync stream. Same active-only gating as
//     sessionIdRewrites.
//
// Re-homed capture_item increments do NOT live here — that mapping must
// survive ACROSS batches (a >100-op offline session splits the colliding
// create from its increments), so it lives in the module-level
// connector-rehome-registry.ts instead. See its header.

export interface DispatchContext {
  assetTypeIdRewrites: Map<string, string>
  sessionIdRewrites: Map<string, string>
  sessionRehomeByBuilding: Map<string, string>
}

export function createDispatchContext(): DispatchContext {
  return {
    assetTypeIdRewrites: new Map<string, string>(),
    sessionIdRewrites: new Map<string, string>(),
    sessionRehomeByBuilding: new Map<string, string>(),
  }
}
